import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Upload, X, ArrowRight, Star, Zap, Award, MapPin, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { DatePickerField } from "@/components/DatePickerField";
import { SelectField } from "@/components/SelectField";
import { useI18n } from "@/context/I18nContext";

const SURFACE = ["Marble", "Granite", "Quartz", "Glass", "Wood", "Furniture", "Kitchen", "Bathroom", "Other"];
const PROPERTY = ["Home", "Office", "Restaurant", "Hotel", "Retail", "Commercial", "Other"];
const TIMES = ["09:00 AM", "11:00 AM", "01:00 PM", "03:00 PM", "05:00 PM"];

export default function Booking() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const pre = location.state || {};
  const [form, setForm] = useState({
    service: pre.service || "", property_type: "", surface_type: pre.surface_type || "", location: "", pincode: "",
    preferred_date: "", preferred_time: "", notes: "", product_id: pre.product_id || null,
    recommended_service: pre.recommended_service || null, estimate: pre.estimate || null,
    material: pre.material || null, scan_id: pre.scan_id || null, quote_id: pre.quote_id || null,
  });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [preference, setPreference] = useState("fastest");
  const [preferredId, setPreferredId] = useState(null);
  const [eligible, setEligible] = useState([]);
  const [loadingElig, setLoadingElig] = useState(false);

  useEffect(() => { track("page_view", { page: "booking" }); }, []);

  const svcForMatch = form.recommended_service || form.service;
  useEffect(() => {
    if (preference !== "choose" || !svcForMatch) { setEligible([]); return; }
    setLoadingElig(true);
    api.get(`/applicators/eligible`, { params: { service: svcForMatch, city: form.location || undefined, pincode: form.pincode || undefined } })
      .then(({ data }) => setEligible(data.applicators || []))
      .catch(() => setEligible([]))
      .finally(() => setLoadingElig(false));
  }, [preference, svcForMatch, form.location, form.pincode]);

  const upload = async (files) => {
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPhotos((p) => [...p, data.url]); }
      catch (_) { toast.error("Upload failed"); }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) { toast.error("Please choose a service"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form, photos, source: "Website",
        applicator_preference: preference,
        preferred_applicator_id: preference === "choose" ? preferredId : null,
      });
      track("booking_submitted", { preference });
      setDone(data);
    } catch (_) { toast.error("Could not create booking"); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="booking-success">
        <CheckCircle2 className="mx-auto text-[#10B981]" size={56} />
        <h1 className="font-display text-4xl mt-6">Booking submitted</h1>
        <p className="mt-4 text-gray-400">Your booking is now in the ERRAH system with status "{done.status}". Track it anytime in MY ERRAH.</p>
        <button onClick={() => navigate("/my-errah")} className="mt-8 inline-flex items-center gap-2 bg-[#C9A45C] text-black px-7 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">Go to MY ERRAH <ArrowRight size={16} /></button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16" data-testid="booking-page">
      <Seo title="Book Installation" description="Book a surface protection installation with ERRAH." />
      <p className="errah-label gold-text mb-4">Booking</p>
      <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tighter">Book an installation.</h1>

      {pre.recommended_service && (
        <div className="mt-6 hair-border bg-white/[0.02] p-4 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="booking-scan-context">
          <span className="errah-label gold-text">From your AI scan</span>
          <span className="text-sm text-gray-200">{pre.recommended_service}</span>
          {pre.estimate && <span className="text-sm text-gray-500">· {pre.estimate}</span>}
        </div>
      )}

      <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="errah-label block mb-2">Service *</label>
          <input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="e.g. Marble Protection" data-testid="b-service"
            className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
        </div>
        <SelectField label="Property type" value={form.property_type} onChange={(v) => setForm({ ...form, property_type: v })} options={PROPERTY} testid="b-property" />
        <SelectField label="Surface type" value={form.surface_type} onChange={(v) => setForm({ ...form, surface_type: v })} options={SURFACE} testid="b-surface" />
        <div>
          <label className="errah-label block mb-2">Location / City</label>
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="b-location" className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
        </div>
        <div>
          <label className="errah-label block mb-2">Pincode</label>
          <input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="b-pincode" className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
        </div>
        <DatePickerField label="Preferred date" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} testid="b-date" />
        <SelectField label="Preferred time" value={form.preferred_time} onChange={(v) => setForm({ ...form, preferred_time: v })} options={TIMES} testid="b-time" />

        <div className="md:col-span-2 mt-2" data-testid="applicator-preference">
          <label className="errah-label block mb-2">{t("preferred_applicator") || "Preferred ERRĀH applicator"}</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[["fastest", Zap, t("pref_fastest")], ["top_rated", Award, t("pref_top_rated")], ["nearest", MapPin, t("pref_nearest")], ["choose", UserCheck, t("pref_choose")]].map(([key, Icon, label]) => (
              <button type="button" key={key} onClick={() => { setPreference(key); if (key !== "choose") setPreferredId(null); }}
                data-testid={`pref-${key}`}
                className={`flex items-center gap-2 px-3 py-3 border text-xs transition-colors ${preference === key ? "border-[#C9A45C] bg-[#C9A45C]/10 text-white" : "border-white/15 text-gray-400 hover:border-white/30"}`}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
          {preference === "choose" && (
            <div className="mt-4" data-testid="choose-applicator-list">
              {!svcForMatch ? <p className="text-xs text-gray-500">Enter a service to see certified applicators.</p>
                : loadingElig ? <p className="text-xs text-gray-500">Finding certified applicators…</p>
                : eligible.length === 0 ? <p className="text-xs text-gray-500">No available certified applicator right now — we'll auto-assign the best match.</p>
                : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {eligible.map((a) => (
                      <div key={a.applicator_code} data-testid={`applicator-card-${a.applicator_code}`}
                        className={`hair-border p-4 transition-colors ${preferredId === a.applicator_code ? "border-[#C9A45C] bg-[#C9A45C]/[0.06]" : "bg-white/[0.02]"}`}>
                        <p className="errah-label gold-text">Certified ERRĀH Applicator</p>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="w-12 h-12 rounded-full bg-[#C9A45C]/15 border border-[#C9A45C]/40 flex items-center justify-center overflow-hidden shrink-0">
                            {a.picture ? <img src={a.picture} alt={a.name} className="w-full h-full object-cover" /> : <UserCheck size={20} className="text-[#C9A45C]" />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm truncate">{a.name}</p>
                            <p className="text-xs text-gray-400 inline-flex items-center gap-1"><Star size={12} className="text-[#C9A45C] fill-[#C9A45C]" /> {a.rating || "—"} · {a.jobs_completed} ERRĀH jobs</p>
                          </div>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-2">{t("certified_for") || "Certified for"}: {(a.certified_services || []).slice(0, 3).join(", ")}</p>
                        <p className="text-[11px] text-gray-500">{t("service_areas")}: {a.service_area || a.city}</p>
                        {a.portfolio_thumbs?.length > 0 && (
                          <div className="flex gap-1.5 mt-2">{a.portfolio_thumbs.map((u, i) => <img key={i} src={u} alt="" className="w-10 h-10 object-cover rounded" />)}</div>
                        )}
                        <div className="flex gap-2 mt-3">
                          <button type="button" onClick={() => window.open(`/applicator-profile/${a.applicator_code}`, "_blank")} data-testid={`view-profile-${a.applicator_code}`} className="text-xs border border-white/20 px-3 py-1.5 hover:bg-white hover:text-black transition-colors">{t("view_profile") || "View Profile"}</button>
                          <button type="button" onClick={() => setPreferredId(a.applicator_code)} data-testid={`select-applicator-${a.applicator_code}`}
                            className={`text-xs px-3 py-1.5 font-semibold transition-colors ${preferredId === a.applicator_code ? "bg-emerald-500 text-black" : "bg-[#C9A45C] text-black hover:bg-[#E4C987]"}`}>
                            {preferredId === a.applicator_code ? (t("selected") || "Selected ✓") : (t("select_applicator") || "Select")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              <p className="text-[11px] text-gray-600 mt-3">If your chosen applicator is unavailable, ERRĀH automatically assigns the next best certified applicator — you're never left waiting.</p>
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="errah-label block mb-2">Notes</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="b-notes" className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
        </div>
        <div className="md:col-span-2">
          <label className="errah-label block mb-2">Photographs</label>
          <div className="flex flex-wrap gap-3">
            {photos.map((url, i) => (
              <div key={i} className="relative w-20 h-20 hair-border overflow-hidden">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/70 p-0.5"><X size={12} /></button>
              </div>
            ))}
            <label className="w-20 h-20 hair-border flex items-center justify-center cursor-pointer hover:border-[#C9A45C] transition-colors" data-testid="b-photo-upload">
              <Upload size={18} className="text-gray-500" />
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
            </label>
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="b-submit"
          className="md:col-span-2 inline-flex items-center justify-center gap-2 bg-[#C9A45C] text-black py-4 text-sm font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-60">
          {busy ? "Submitting…" : <>Submit booking <ArrowRight size={16} /></>}
        </button>
      </form>
    </div>
  );
}
